/**
 * BrokerClientIndexManager - 交易商客户索引管理器
 * 
 * 功能：
 * 1. 维护交易商 → 客户的映射关系
 * 2. 快速查询交易商的所有客户
 * 3. 支持 10000+ 用户的高性能查询
 */

import { logger } from '../utils/logger';
import { storage } from '../utils/storage';
import type { UserManager } from './UserManager';

const STORAGE_KEY = 'broker_client_index';

export class BrokerClientIndexManager {
  private brokerClients: Map<string, Set<string>> = new Map();
  // brokerUid → Set<clientUid>

  constructor() {
    this.loadIndex();
    logger.log('BrokerClientIndexManager initialized');
  }

  /**
   * 添加客户到交易商索引
   */
  addClient(brokerUid: string, clientUid: string): void {
    if (!this.brokerClients.has(brokerUid)) {
      this.brokerClients.set(brokerUid, new Set());
    }

    const clients = this.brokerClients.get(brokerUid)!;
    clients.add(clientUid);

    this.saveIndex();

    logger.log(`✅ 客户 ${clientUid} 已添加到交易商 ${brokerUid} 的索引`);
  }

  /**
   * 从交易商索引中移除客户
   */
  removeClient(brokerUid: string, clientUid: string): void {
    const clients = this.brokerClients.get(brokerUid);
    if (clients) {
      clients.delete(clientUid);
      this.saveIndex();
      logger.log(`✅ 客户 ${clientUid} 已从交易商 ${brokerUid} 的索引中移除`);
    }
  }

  /**
   * 更换客户的交易商
   */
  changeClientBroker(clientUid: string, oldBrokerUid: string, newBrokerUid: string): void {
    this.removeClient(oldBrokerUid, clientUid);
    this.addClient(newBrokerUid, clientUid);
    logger.log(`✅ 客户 ${clientUid} 已从交易商 ${oldBrokerUid} 转移到 ${newBrokerUid}`);
  }

  /**
   * 获取交易商的所有客户UID
   */
  getClientUids(brokerUid: string): string[] {
    const clients = this.brokerClients.get(brokerUid);
    return clients ? Array.from(clients) : [];
  }

  /**
   * 获取交易商的客户数量
   */
  getClientCount(brokerUid: string): number {
    const clients = this.brokerClients.get(brokerUid);
    return clients ? clients.size : 0;
  }

  /**
   * 获取交易商的所有客户对象
   */
  getClients(brokerUid: string, userManager: UserManager): any[] {
    const clientUids = this.getClientUids(brokerUid);
    return clientUids
      .map(uid => userManager.getUserByUID(uid))
      .filter(user => user !== null);
  }

  /**
   * 获取所有交易商及其客户数量
   */
  getAllBrokerStats(): Map<string, number> {
    const stats = new Map<string, number>();
    for (const [brokerUid, clients] of this.brokerClients) {
      stats.set(brokerUid, clients.size);
    }
    return stats;
  }

  /**
   * 保存索引到 localStorage
   */
  private saveIndex(): void {
    const data: Record<string, string[]> = {};
    for (const [brokerUid, clientUids] of this.brokerClients) {
      data[brokerUid] = Array.from(clientUids);
    }
    storage.set(STORAGE_KEY, data);
  }

  /**
   * 从 localStorage 加载索引
   */
  private loadIndex(): void {
    const data = storage.get<Record<string, string[]>>(STORAGE_KEY);
    if (data) {
      for (const [brokerUid, clientUids] of Object.entries(data)) {
        this.brokerClients.set(brokerUid, new Set(clientUids));
      }
      logger.log(`📦 已加载交易商客户索引: ${this.brokerClients.size} 个交易商`);
    }
  }

  /**
   * 清空所有索引（测试用）
   */
  clearAll(): void {
    this.brokerClients.clear();
    storage.remove(STORAGE_KEY);
    logger.log('🗑️  所有交易商客户索引已清空');
  }
}
